import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Printer, FileDown, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errMsg, downloadFile } from "../lib/api";
import { ResearchBadge } from "../components/Badges";
import { PolledProgress } from "../components/ResearchProgress";
import ReportView from "../components/report/ReportView";
import LeadPanel from "../components/report/LeadPanel";
import OwnerIntel from "../components/report/OwnerIntel";

const ACTIVE = ["queued", "running"];

export default function LeadReport() {
  const { id } = useParams();
  const [loc, setLoc] = useState(null);
  const [data, setData] = useState(null);
  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState("latest");
  const [services, setServices] = useState({});
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const v = version === "latest" ? "" : `&version=${version}`;
      const [l, r, vs] = await Promise.all([api.get(`/locations/${id}`), api.get(`/locations/${id}/report?audience=internal${v}`), api.get(`/locations/${id}/reports`)]);
      setLoc(l.data);
      setData(r.data);
      setVersions(vs.data);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [id, version]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(Object.fromEntries(r.data.map((s) => [s.key, s.name])))).catch(() => {});
  }, []);

  const rerun = async () => {
    try {
      await api.post("/research", { location_ids: [id] });
      toast.success("Research started. Confirmed findings will be preserved.");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const updateFinding = (f) => setData((d) => ({ ...d, findings: d.findings.map((x) => (x.id === f.id ? f : x)) }));

  if (error) return <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700" data-testid="report-error">{error}</div>;
  if (!loc || !data) return <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading report…</div>;
  const job = loc.latest_job;
  const running = job && ACTIVE.includes(job.status);
  const slug = loc.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  return (
    <div className="space-y-6">
      <Link to="/leads" className="no-print inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900"><ArrowLeft className="h-3.5 w-3.5" />Saved leads</Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-orange-600">Opportunity report</div>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight" data-testid="lead-title">{loc.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">{loc.address} <ResearchBadge status={loc.research_status} testId="lead-research-status" /><span className="text-xs">· {loc.qualification_status}</span></div>
        </div>
        <div className="no-print flex flex-wrap gap-2">
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => downloadFile(`/locations/${id}/call-sheet.pdf`, `${slug}-owner-call-sheet.pdf`).then(() => toast.success("Owner call sheet downloaded")).catch((e) => toast.error(errMsg(e)))} data-testid="generate-call-sheet-btn">GENERATE OWNER CALL SHEET</Button>
          <Button size="sm" variant="outline" onClick={rerun} disabled={running} data-testid="rerun-research-btn"><RefreshCw className="mr-1 h-3.5 w-3.5" />{data.report ? "Re-run research" : "Run research"}</Button>
          {data.report && (
            <>
              <Button size="sm" variant="outline" asChild data-testid="print-internal-btn"><a href={`/print/${id}?audience=internal`} target="_blank" rel="noreferrer"><Printer className="mr-1 h-3.5 w-3.5" />Internal print/PDF</a></Button>
              <Button size="sm" className="bg-orange-600 hover:bg-orange-700" asChild data-testid="print-prospect-btn"><a href={`/print/${id}?audience=prospect`} target="_blank" rel="noreferrer"><Printer className="mr-1 h-3.5 w-3.5" />Prospect report/PDF</a></Button>
              <Button size="sm" variant="outline" onClick={() => downloadFile(`/locations/${id}/findings.csv?audience=internal`, `${slug}-findings.csv`).catch((e) => toast.error(errMsg(e)))} data-testid="export-findings-csv-btn"><FileDown className="mr-1 h-3.5 w-3.5" />CSV</Button>
            </>
          )}
        </div>
      </div>

      {running && <PolledProgress job={job} onFinish={load} />}
      {!running && job && ["failed", "partial", "cancelled"].includes(job.status) && (
        <details className="rounded-lg border bg-white p-4 text-sm" data-testid="last-job-details"><summary className="cursor-pointer font-semibold">Last research run: {job.status} — view stage details</summary><div className="mt-3"><PolledProgress job={job} /></div></details>
      )}

      {!data.report && !running && (
        <div className="rounded-lg border border-dashed bg-white p-10 text-center" data-testid="report-not-researched">
          <div className="text-lg font-semibold">Not researched yet</div>
          <p className="mt-1 text-sm text-slate-500">No findings exist. Run research to generate a sourced report. Nothing is shown as "no gaps" until research runs.</p>
          <Button className="mt-4 bg-orange-600 hover:bg-orange-700" onClick={rerun} data-testid="run-research-empty-btn">Research & Generate Report</Button>
        </div>
      )}

      <Tabs defaultValue="report">
        <TabsList className="no-print">
          <TabsTrigger value="report" data-testid="tab-report">Report</TabsTrigger>
          <TabsTrigger value="owner" data-testid="tab-owner">Owner & Contact Intelligence</TabsTrigger>
          <TabsTrigger value="lead" data-testid="tab-lead">Lead & qualification</TabsTrigger>
        </TabsList>
        <TabsContent value="report" className="mt-4">
          {data.report && (
            <>
              {versions.length > 1 && (
                <div className="no-print mb-4 flex items-center gap-2 text-sm">Version
                  <Select value={String(version)} onValueChange={setVersion}>
                    <SelectTrigger className="w-44" data-testid="report-version-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="latest">Latest (live findings)</SelectItem>
                      {versions.map((v) => <SelectItem key={v.id} value={String(v.version)}>v{v.version} snapshot</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <ReportView data={data} audience="internal" editable={version === "latest"} onChanged={updateFinding} services={services} />
            </>
          )}
        </TabsContent>
        <TabsContent value="owner" className="mt-4"><OwnerIntel loc={loc} reload={load} /></TabsContent>
        <TabsContent value="lead" className="mt-4"><LeadPanel loc={loc} reload={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
